import { SpaceVersion } from '@lighthouse/core'
import type { OtherPlatformType } from '@lighthouse/shared'
import { spaceVersionEnum, useSpaceQuotaContext } from '@lighthouse/shared'
import mitt from 'mitt'
import React, { useCallback, useEffect, useState } from 'react'
import { useImmer } from 'use-immer'

import {
    AliyunCodeAccountModal,
    DeepSeekAccountModal,
    ErnieBotAccountModal,
    MoonAccountModal,
    NetEaseFreeAccountModal,
    QwenAccountModal,
    SearchAccountModal,
    StripeAccountModal,
    WeChatOfficialAccountModal,
    WeChatPayAccountModal,
    WeComEmailAccountModal,
    ZhiPuAccountModal
} from '@/components/OtherPlatFormModal'
import { openSpaceGradeConfirm } from '@/components/SpaceGrade'

import { advancedOtherPlatFormList } from './constant'
import OtherPlatformInstallModal from './InstallConfirmModal'

type openOtherPlatformModalParams = { type: OtherPlatformType; options?: { isPerfectWebsiteInfo: boolean } }

type Event = {
    openOtherPlatform: openOtherPlatformModalParams
    openInstallOtherPlatform: OtherPlatformType
}

export const otherPlatformEmitter = mitt<Event>()

export const openOtherPlatform = (params: openOtherPlatformModalParams) => {
    otherPlatformEmitter.emit('openOtherPlatform', params)
}

export const openInstallOtherPlatform = (params: OtherPlatformType) => {
    otherPlatformEmitter.emit('openInstallOtherPlatform', params)
}

export const OtherPlatformEvent = ({ children }: { children: React.ReactNode }) => {
    const [typeMap, setTypeMap] = useImmer<Record<OtherPlatformType, boolean>>({
        ALIYUN_SMS: false,
        DING_TALK_ROBOT: false,
        NET_EASE_126_MAIL: false,
        WECHAT_OFFICIAL_ACCOUNT: false,
        WECHAT_PAY: false,
        WE_COM_MAIL: false,
        WE_COM_ROBOT: false,
        STRIPE: false,
        SEARCH_API: false,
        QWEN: false,
        ERNIE_BOT: false,
        ZHI_PU_AI: false,
        MOONSHOT_AI: false,
        DEEP_SEEK: false,
        WECHAT_MINI_PROGRAM: false
    })
    const { quota: spaceQuota } = useSpaceQuotaContext()
    const [installOpenType, setInstallOpenType] = useState<OtherPlatformType | undefined>(undefined)
    const [options, setOptions] = useState<{ isPerfectWebsiteInfo: boolean } | undefined>(undefined)

    const handleOpen = useCallback(
        (params: openOtherPlatformModalParams) => {
            const { type, options } = params
            if (
                advancedOtherPlatFormList.includes(type) &&
                (spaceQuota?.currentVersionCode ?? spaceVersionEnum.FREE) < spaceVersionEnum.ADVANCED
            ) {
                openSpaceGradeConfirm(SpaceVersion.ADVANCED)
                return
            }
            setTypeMap(draft => {
                draft[type] = true
            })
            setOptions(options ?? { isPerfectWebsiteInfo: false })
        },
        [setTypeMap, spaceQuota?.currentVersionCode]
    )

    const handleInstallOpen = useCallback((type: OtherPlatformType) => {
        setInstallOpenType(type)
    }, [])

    const handleInstallClose = useCallback(() => {
        setInstallOpenType(undefined)
    }, [])

    const handleClose = useCallback(
        (type: OtherPlatformType) => {
            setTypeMap(draft => {
                draft[type] = false
            })
            setOptions(undefined)
        },
        [setTypeMap]
    )
    useEffect(() => {
        otherPlatformEmitter.on('openOtherPlatform', handleOpen)
        otherPlatformEmitter.on('openInstallOtherPlatform', handleInstallOpen)

        return () => {
            otherPlatformEmitter.off('openOtherPlatform', handleOpen)
            otherPlatformEmitter.off('openInstallOtherPlatform', handleInstallOpen)
        }
    }, [handleInstallOpen, handleOpen])

    return (
        <>
            {children}
            <WeComEmailAccountModal open={typeMap['WE_COM_MAIL']} onClose={() => handleClose('WE_COM_MAIL')} />
            <NetEaseFreeAccountModal open={typeMap['NET_EASE_126_MAIL']} onClose={() => handleClose('NET_EASE_126_MAIL')} />
            <AliyunCodeAccountModal open={typeMap['ALIYUN_SMS']} onClose={() => handleClose('ALIYUN_SMS')} />
            <WeChatOfficialAccountModal
                isPerfectWebsiteInfo={options?.isPerfectWebsiteInfo}
                open={typeMap['WECHAT_OFFICIAL_ACCOUNT']}
                onClose={() => handleClose('WECHAT_OFFICIAL_ACCOUNT')}
            />
            <WeChatPayAccountModal open={typeMap['WECHAT_PAY']} onClose={() => handleClose('WECHAT_PAY')} />
            <StripeAccountModal open={typeMap['STRIPE']} onClose={() => handleClose('STRIPE')} />
            <ZhiPuAccountModal open={typeMap['ZHI_PU_AI']} onClose={() => handleClose('ZHI_PU_AI')} />
            <SearchAccountModal open={typeMap['SEARCH_API']} onClose={() => handleClose('SEARCH_API')} />
            <QwenAccountModal open={typeMap['QWEN']} onClose={() => handleClose('QWEN')} />
            <ErnieBotAccountModal open={typeMap['ERNIE_BOT']} onClose={() => handleClose('ERNIE_BOT')} />
            <DeepSeekAccountModal open={typeMap['DEEP_SEEK']} onClose={() => handleClose('DEEP_SEEK')} />
            <MoonAccountModal open={typeMap['MOONSHOT_AI']} onClose={() => handleClose('MOONSHOT_AI')} />

            <OtherPlatformInstallModal type={installOpenType} onClose={handleInstallClose} />
        </>
    )
}
